import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight,
  CircleCheck,
  CircleX,
  Clapperboard,
  CreditCard,
  ExternalLink,
  Instagram,
  Loader2,
  MessageSquare,
  Phone,
  Play,
  Send,
  TriangleAlert,
  User,
  Wallet,
  X,
  XCircle,
} from '@/lib/icons'
import type {
  CommentItem,
  RejectReason as RejectReasonType,
  VideoCheckItem,
  VideoCheckPatchRequest,
  VideoCheckRequest,
  VideoCheckResponse,
  VideoDetail as VideoDetailData,
} from '@ayub/shared'
import { calculateAmount, formatNumber, formatSom, RejectReason, VideoStage } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Modal } from '@/components/ui/modal'
import { RejectReasonPicker } from '@/components/ui/reject-reason-picker'
import { Skeleton } from '@/components/ui/skeleton'
import { HistorySection } from '@/components/history/HistorySection'
import { ApiError } from '@/lib/api'
import { formatDateUz, formatRelativeUz } from '@/lib/relative-date'
import { openArchiveLink } from '@/lib/telegram'
import { cn } from '@/lib/utils'
import {
  addVideoComment,
  fetchVideo,
  moderateVideo,
  patchVideoCheck,
  submitVideoCheck,
  verifyVideoLink,
} from './api'
import { VideoStageBadge } from './VideoStageBadge'
import { ChecksSection, EditCheckModal, ScreenshotModal } from './VideoChecksSection'
import { isAwaitingLink, linkVerifyUrgency, urgencyLabel, VIDEO_URGENCY_META } from './urgency'

export interface VideoDetailProps {
  /** `null` bo'lsa kartochka yopiq — hech narsa render qilinmaydi (Modal o'zi buni boshqaradi). */
  videoId: number | null
  onClose: () => void
  /** "Bloger kartochkasi →" bosilganda — video kartochkasi yopilib, bloger kartochkasi ochiladi. */
  onOpenBlogger: (bloggerId: number) => void
}

/**
 * Video rad etish sabablari faqat mantiqan video bilan bog'liqlari — vazifa talabiga ko'ra
 * qat'iy ro'yxat: sifat past / kitob ko'rinmayapti / mos kelmaydigan kontent / boshqa. Matn
 * baribir `REJECT_REASON_LABELS`dan olinadi (`ui/reject-reason-picker.tsx`) — bu yerda hech
 * narsa qayta yozilmaydi.
 */
const VIDEO_REJECT_REASONS: readonly RejectReasonType[] = [
  RejectReason.LOW_QUALITY,
  RejectReason.BOOK_NOT_VISIBLE,
  RejectReason.IRRELEVANT_CONTENT,
  RejectReason.OTHER,
]

/**
 * Havola tekshiruvida rad etish sabablari (Bosqich 8, `docs/03-app-flow.md` "Havola
 * tekshiruvi") — moderatsiya sabablaridan FARQLI to'plam: bu yerda "sifat past" yoki
 * "kitob ko'rinmayapti" mantiqsiz (video allaqachon tasdiqlangan, faqat HAVOLA
 * tekshirilmoqda). Havola o'lik/noto'g'ri bo'lishi mumkin (`LINK_DEAD`), begona/mos
 * kelmaydigan post bo'lishi mumkin (`IRRELEVANT_CONTENT`), yoki boshqa sabab (`OTHER`).
 */
const LINK_REJECT_REASONS: readonly RejectReasonType[] = [
  RejectReason.LINK_DEAD,
  RejectReason.IRRELEVANT_CONTENT,
  RejectReason.OTHER,
]

/** `+998901234567` → `+998 90 123 45 67`. Formatga mos kelmasa xom qiymat qaytadi. */
function formatPhoneDisplay(phone: string): string {
  const match = /^\+998(\d{2})(\d{3})(\d{2})(\d{2})$/.exec(phone)
  if (!match) return phone
  const [, a, b, c, d] = match
  return `+998 ${a} ${b} ${c} ${d}`
}

/**
 * Video kartochkasi — `docs/03-app-flow.md` "Video kartochkasi (ochilganda)".
 *
 * Layout naqshi `components/bloggers/BloggerDetail.tsx` bilan bir xil: header/scroll-body/
 * footer flexbox qatlamlari orqali "asosiy amallar pastda yopishib turadi" qoidasi CSS
 * darajasida kafolatlanadi (`docs/04-ui-ux.md`) — 375px'da ham Tasdiqlash/Rad etish
 * tugmalari hech qachon skroll ortida qolmaydi.
 */
export function VideoDetail({ videoId, onClose, onOpenBlogger }: VideoDetailProps) {
  const queryClient = useQueryClient()

  const [commentText, setCommentText] = useState('')
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false)
  const [rejectPickerOpen, setRejectPickerOpen] = useState(false)
  const [linkRejectPickerOpen, setLinkRejectPickerOpen] = useState(false)

  // Bosqich 9 — tekshiruv kiritish/tuzatish holati. Video uchun BITTA tekshiruv saqlanganda
  // hisob YOPILADI va summa balansga qo'shiladi — qaytarib bo'lmaydigan amal, shuning uchun
  // to'g'ridan-to'g'ri mutatsiya chaqirilmaydi, avval `ConfirmDialog` orqali aniq tasdiq
  // so'raladi (docs/04-ui-ux.md: "Xavfli amallar doim tasdiq oynasi bilan").
  const [pendingFinalize, setPendingFinalize] = useState<{ views: number } | null>(null)
  const [editingCheck, setEditingCheck] = useState<VideoCheckItem | null>(null)
  // Vazifa 2 — skrinshot ko'rish (`VideoChecksSection.tsx`dagi `ScreenshotModal`).
  // `fileId` shu yerda saqlanadi (modal komponentning o'zi emas — u faqat `null`/string
  // qiymatga qarab ochiladi/yopiladi, `EditCheckModal`dagi `editingCheck` bilan bir xil naqsh).
  const [screenshotFileId, setScreenshotFileId] = useState<string | null>(null)
  const [pendingEditFinalize, setPendingEditFinalize] = useState<{
    views: number
    note: string | undefined
    oldViews: number
  } | null>(null)
  // `VideoCheckResponse.cardRequested` — faqat mutatsiya javobida keladi, `VideoDetail`
  // shaklida saqlanmaydi, shuning uchun mahalliy holatda ushlab turiladi. Toast EMAS: bu
  // ma'lumot vaqtinchalik yo'qolib qolmasligi kerak (adminga to'lov ro'yxatidagi kelajakdagi
  // ⚠️ belgisini tushuntiradi) — kartochka ochiq turgan ekan, ko'rinib turadi.
  const [cardRequestedNotice, setCardRequestedNotice] = useState(false)

  const detailQuery = useQuery<VideoDetailData, Error>({
    queryKey: ['video', videoId],
    queryFn: () => {
      if (videoId === null) throw new Error("Video ID yo'q")
      return fetchVideo(videoId)
    },
    enabled: videoId !== null,
  })

  const moderateMutation = useMutation<
    VideoDetailData,
    Error,
    { approved: boolean; reason?: RejectReasonType; note?: string }
  >({
    mutationFn: (body) => {
      if (videoId === null) throw new Error("Video ID yo'q")
      return moderateVideo(videoId, body)
    },
    onSuccess: (updated) => {
      // Server yangilangan to'liq kartochkani qaytaradi — darhol keshga yozamiz, shunda
      // bosqich belgisi va tugmalar bir zumda yangilanadi (qo'shimcha so'rovsiz).
      queryClient.setQueryData(['video', videoId], updated)
      invalidateRelatedCaches()
      setConfirmApproveOpen(false)
      setRejectPickerOpen(false)
    },
  })

  const verifyLinkMutation = useMutation<
    VideoDetailData,
    Error,
    { ok: boolean; reason?: RejectReasonType; note?: string }
  >({
    mutationFn: (body) => {
      if (videoId === null) throw new Error("Video ID yo'q")
      return verifyVideoLink(videoId, body)
    },
    onSuccess: (updated) => {
      // `moderateMutation.onSuccess` bilan bir xil naqsh — server yangilangan to'liq
      // kartochkani qaytaradi, darhol keshga yoziladi.
      queryClient.setQueryData(['video', videoId], updated)
      invalidateRelatedCaches()
      setLinkRejectPickerOpen(false)
    },
  })

  const commentMutation = useMutation<CommentItem, Error, string>({
    mutationFn: (text) => {
      if (videoId === null) throw new Error("Video ID yo'q")
      return addVideoComment(videoId, text)
    },
    onSuccess: () => {
      setCommentText('')
      void queryClient.invalidateQueries({ queryKey: ['video', videoId] })
    },
  })

  const submitCheckMutation = useMutation<VideoCheckResponse, Error, VideoCheckRequest>({
    mutationFn: (body) => {
      if (videoId === null) throw new Error("Video ID yo'q")
      return submitVideoCheck(videoId, body)
    },
    onSuccess: (response) => {
      // `VideoCheckResponse.video` — server qaytargan YANGILANGAN to'liq kartochka
      // (`moderateMutation.onSuccess` bilan bir xil naqsh): qo'shimcha so'rovsiz darhol
      // keshga yoziladi, shunda "kutilmoqda" inputi kartochkadan yo'qolib, o'rniga ✅
      // qatordagi saqlangan qiymat ko'rinadi — bu ekranning "aniq muvaffaqiyat holati".
      queryClient.setQueryData(['video', videoId], response.video)
      invalidateRelatedCaches()
      setPendingFinalize(null)
      setCardRequestedNotice(response.cardRequested)
    },
  })

  const patchCheckMutation = useMutation<VideoCheckResponse, Error, VideoCheckPatchRequest>({
    mutationFn: (body) => {
      if (videoId === null) throw new Error("Video ID yo'q")
      return patchVideoCheck(videoId, body)
    },
    onSuccess: (response) => {
      queryClient.setQueryData(['video', videoId], response.video)
      invalidateRelatedCaches()
      setEditingCheck(null)
      setPendingEditFinalize(null)
    },
  })

  /**
   * Moderatsiya qarori videolar ro'yxati/kanban, Dashboard hisoblagichlarini va (arzon
   * bo'lgani uchun) blogerlar keshini eskirtiradi — bloger kartochkasidagi "Videolari"
   * ro'yxati va moliya xulosasi shu videoga bog'liq. Bu keng invalidatsiya faqat MOUNT
   * bo'lgan so'rovlarni qayta yuklaydi, shuning uchun "arzon": ochilmagan sahifalar uchun
   * hech qanday tarmoq so'rovi yubormaydi.
   */
  function invalidateRelatedCaches() {
    void queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0]
        return key === 'videos' || key === 'dashboard' || key === 'bloggers'
      },
    })
  }

  // Yangi video ochilganda (yoki karta yopilganda) — oldingi holatlar qolib ketmasin. Ayniqsa
  // muhim: oldingi videoning "karta so'raldi" bildirishnomasi yoki tuzatish oynasi hech qachon
  // keyingi videoga "ko'chib o'tmasligi" kerak (`BloggerDetail.tsx`dagi bir xil naqsh —
  // mutatsiya `.reset()`lari ataylab shu yerda, dependency ro'yxatida ATAYLAB yo'q, aks holda
  // har bir mutatsiya holati o'zgarishida (masalan saqlash paytida) forma tozalanib ketardi).
  useEffect(() => {
    setCommentText('')
    setConfirmApproveOpen(false)
    setRejectPickerOpen(false)
    setLinkRejectPickerOpen(false)
    setPendingFinalize(null)
    setEditingCheck(null)
    setPendingEditFinalize(null)
    setCardRequestedNotice(false)
    setScreenshotFileId(null)
    submitCheckMutation.reset()
    patchCheckMutation.reset()
  }, [videoId])

  function handleApproveConfirm() {
    moderateMutation.mutate({ approved: true })
  }

  function handleRejectSubmit(reason: RejectReasonType, note: string | undefined) {
    moderateMutation.mutate({ approved: false, reason, note })
  }

  function handleVerifyLinkOk() {
    verifyLinkMutation.mutate({ ok: true })
  }

  function handleVerifyLinkRejectSubmit(reason: RejectReasonType, note: string | undefined) {
    verifyLinkMutation.mutate({ ok: false, reason, note })
  }

  function handleAddComment(event: FormEvent) {
    event.preventDefault()
    const trimmed = commentText.trim()
    if (trimmed.length === 0) return
    commentMutation.mutate(trimmed)
  }

  /**
   * Video uchun YAGONA tekshiruv — saqlansa hisob YOPILADI, shuning uchun to'g'ridan-to'g'ri
   * saqlanmaydi: avval `pendingFinalize` orqali oqibatni tushuntiruvchi `ConfirmDialog`
   * ochiladi (pastda). (docs/04-ui-ux.md: "Xavfli amallar doim tasdiq oynasi bilan").
   */
  function handleSubmitCheck(views: number) {
    setPendingFinalize({ views })
  }

  function handleConfirmFinalize() {
    if (!pendingFinalize) return
    submitCheckMutation.mutate({ views: pendingFinalize.views })
  }

  function handleCancelFinalize() {
    submitCheckMutation.reset()
    setPendingFinalize(null)
  }

  function handleRequestEditCheck(check: VideoCheckItem) {
    patchCheckMutation.reset()
    setEditingCheck(check)
  }

  function handleCancelEditCheck() {
    patchCheckMutation.reset()
    setEditingCheck(null)
  }

  /**
   * Tekshiruvni tuzatish blogerga tegishli YAKUNIY to'lov miqdorini o'zgartiradi (yagona
   * tekshiruv hisobni yopgan) — `EditCheckModal`ning o'zi yetarli tasdiq emas,
   * `pendingEditFinalize` orqali ikkinchi (aniq) tasdiq bosqichi ochiladi.
   */
  function handleSubmitEditCheck(views: number, note: string | undefined) {
    if (!editingCheck) return
    setPendingEditFinalize({ views, note, oldViews: editingCheck.views })
    setEditingCheck(null)
  }

  function handleConfirmEditFinalize() {
    if (!pendingEditFinalize) return
    patchCheckMutation.mutate({
      views: pendingEditFinalize.views,
      note: pendingEditFinalize.note,
    })
  }

  function handleCancelEditFinalize() {
    patchCheckMutation.reset()
    setPendingEditFinalize(null)
  }

  const detailError = detailQuery.error
  const moderateError = moderateMutation.error
  const commentError = commentMutation.error
  const verifyLinkError = verifyLinkMutation.error
  const submitCheckError = submitCheckMutation.error ? submitCheckMutation.error.message : null
  const patchCheckError = patchCheckMutation.error ? patchCheckMutation.error.message : null

  return (
    <>
      <Modal
        open={videoId !== null}
        onClose={onClose}
        ariaLabel={
          detailQuery.data ? `Video #${detailQuery.data.id} — kartochka` : 'Video kartochkasi'
        }
      >
        {detailQuery.isLoading && <DetailSkeleton />}

        {!detailQuery.isLoading && detailQuery.isError && (
          <DetailErrorState
            error={detailError}
            onRetry={() => void detailQuery.refetch()}
            onClose={onClose}
          />
        )}

        {!detailQuery.isLoading && detailQuery.data && (
          <div className="flex h-full min-h-0 flex-col">
            <IdentityHeader
              video={detailQuery.data}
              onClose={onClose}
              onOpenBlogger={onOpenBlogger}
            />

            {/* `overscroll-contain` — iOS Safari'da bu ichki skroll oxiriga yetganda "rubber-band"
                effekti orqadagi sahifaga (modal orqasidagi body) o'tib ketmasin, aks holda
                `body.style.overflow = 'hidden'` (modal.tsx) yetarli bo'lmaydi. */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <div className="flex flex-col gap-4 p-4">
                <VideoPlaybackSection archiveUrl={detailQuery.data.archiveUrl} />
                <InstagramSection
                  video={detailQuery.data}
                  isPending={verifyLinkMutation.isPending}
                  errorMessage={verifyLinkError ? verifyLinkError.message : null}
                  onVerifyOk={handleVerifyLinkOk}
                  onVerifyReject={() => setLinkRejectPickerOpen(true)}
                />
                <ChecksSection
                  video={detailQuery.data}
                  onSubmitCheck={handleSubmitCheck}
                  isSubmitting={submitCheckMutation.isPending}
                  submitError={submitCheckError}
                  onRequestEdit={handleRequestEditCheck}
                  onViewScreenshot={setScreenshotFileId}
                />
                {cardRequestedNotice && (
                  <p className="inline-flex items-start gap-1.5 rounded-card border border-urgency-today/30 bg-urgency-today/10 px-3 py-2 text-xs font-medium text-urgency-today-text">
                    <CreditCard size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                    Blogerdan karta ma&apos;lumoti so&apos;raldi (avval yo&apos;q edi) — 5-sana
                    to&apos;lov ro&apos;yxatida bu bloger ogohlantirish belgisi bilan chiqadi.
                  </p>
                )}
                <FinanceSection video={detailQuery.data} />
                <CommentsSection
                  comments={detailQuery.data.comments}
                  commentText={commentText}
                  onCommentTextChange={setCommentText}
                  onSubmit={handleAddComment}
                  isSubmitting={commentMutation.isPending}
                  errorMessage={commentError ? commentError.message : null}
                />
                {/* Izohlardan KEYIN, kartochka pastida — `BloggerDetail.tsx`dagi bilan bir
                    xil joylashuv qarori. */}
                <HistorySection entityType="video" entityId={detailQuery.data.id} />
              </div>
            </div>

            <DecisionFooter
              stage={detailQuery.data.stage}
              moderatedAt={detailQuery.data.moderatedAt}
              isPending={moderateMutation.isPending}
              errorMessage={moderateError ? moderateError.message : null}
              onApproveClick={() => setConfirmApproveOpen(true)}
              onRejectClick={() => setRejectPickerOpen(true)}
            />
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={confirmApproveOpen}
        title="Videoni tasdiqlaysizmi?"
        description="Bloger Instagram'ga joylashi va havolasini yuborishi mumkin bo'ladi. Bloger bildirishnoma oladi."
        confirmLabel="Ha, tasdiqlash"
        tone="primary"
        isLoading={moderateMutation.isPending}
        onConfirm={handleApproveConfirm}
        onCancel={() => setConfirmApproveOpen(false)}
      />

      <RejectReasonPicker
        open={rejectPickerOpen}
        isSubmitting={moderateMutation.isPending}
        reasons={VIDEO_REJECT_REASONS}
        onCancel={() => setRejectPickerOpen(false)}
        onSubmit={handleRejectSubmit}
      />

      <RejectReasonPicker
        open={linkRejectPickerOpen}
        isSubmitting={verifyLinkMutation.isPending}
        reasons={LINK_REJECT_REASONS}
        onCancel={() => setLinkRejectPickerOpen(false)}
        onSubmit={handleVerifyLinkRejectSubmit}
      />

      {/*
        Tekshiruvni SAQLASH tasdiqi — docs/03: "hisob yopiladi, summa balansga qo'shiladi".
        Oqibat aniq so'mda ko'rsatiladi, `calculateAmount()` orqali (`@ayub/shared` — pastda
        `AmountPreview`dagi bilan bir xil sabab: mustaqil formula yozilsa jonli ko'rsatkich
        bilan kelishmovchilik xavfi bor).
      */}
      <ConfirmDialog
        open={pendingFinalize !== null}
        title="Tekshiruvni saqlaysizmi?"
        description={
          pendingFinalize && detailQuery.data ? (
            <>
              Bu — video #{detailQuery.data.id} bo&apos;yicha <strong>YAKUNIY</strong> tekshiruv.
              Saqlagandan so&apos;ng hisob <strong>yopiladi</strong> va{' '}
              <strong className="text-app-text">
                {formatSom(
                  calculateAmount(pendingFinalize.views, detailQuery.data.ratePer1000 ?? 0),
                )}
              </strong>{' '}
              {detailQuery.data.blogger.fullName}ning balansiga qo&apos;shiladi. Bu amalni orqaga
              qaytarib bo&apos;lmaydi.
            </>
          ) : null
        }
        confirmLabel="Ha, yakunlash"
        tone="primary"
        isLoading={submitCheckMutation.isPending}
        errorMessage={submitCheckError}
        onConfirm={handleConfirmFinalize}
        onCancel={handleCancelFinalize}
      />

      <EditCheckModal
        check={editingCheck}
        ratePer1000={detailQuery.data?.ratePer1000 ?? 0}
        isSubmitting={patchCheckMutation.isPending}
        errorMessage={patchCheckError}
        onCancel={handleCancelEditCheck}
        onSubmit={handleSubmitEditCheck}
      />

      <ScreenshotModal fileId={screenshotFileId} onClose={() => setScreenshotFileId(null)} />

      {/* Yakuniy tekshiruvni TUZATISH tasdiqi — blogerga tegishli allaqachon balansga
          qo'shilgan summani o'zgartiradi, shuning uchun `danger` ohang bilan alohida so'raladi. */}
      <ConfirmDialog
        open={pendingEditFinalize !== null}
        title="Yakuniy tekshiruvni tuzataysizmi?"
        description={
          pendingEditFinalize && detailQuery.data ? (
            <>
              Bu <strong>YAKUNIY</strong> tekshiruv — natija allaqachon blogerning balansiga
              qo&apos;shilgan. Prosmotrni{' '}
              <strong>{formatNumber(pendingEditFinalize.oldViews)}</strong> dan{' '}
              <strong>{formatNumber(pendingEditFinalize.views)}</strong> ga o&apos;zgartirasiz,
              yangi summa:{' '}
              <strong className="text-app-text">
                {formatSom(
                  calculateAmount(pendingEditFinalize.views, detailQuery.data.ratePer1000 ?? 0),
                )}
              </strong>
              . Farq blogerning balansida hisobga olinadi.
            </>
          ) : null
        }
        confirmLabel="Ha, tuzatish"
        tone="danger"
        isLoading={patchCheckMutation.isPending}
        errorMessage={patchCheckError}
        onConfirm={handleConfirmEditFinalize}
        onCancel={handleCancelEditFinalize}
      />
    </>
  )
}

interface IdentityHeaderProps {
  video: VideoDetailData
  onClose: () => void
  onOpenBlogger: (bloggerId: number) => void
}

/** Modalning skroll qilinmaydigan yuqori qismi — video raqami, bosqich va bloger identligi. */
function IdentityHeader({ video, onClose, onOpenBlogger }: IdentityHeaderProps) {
  return (
    <div className="shrink-0 border-b border-app-border p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg leading-tight font-semibold">Video #{video.id}</h1>
          <VideoStageBadge stage={video.stage} className="mt-1" />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Yopish"
          className="flex size-11 shrink-0 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-app-bg"
        >
          <X size={20} aria-hidden="true" />
        </button>
      </div>

      <div className="flex flex-col gap-1 text-sm">
        <span className="inline-flex items-center gap-2 font-medium text-app-text">
          <User size={14} className="shrink-0 text-app-hint" aria-hidden="true" />
          <span className="truncate">@{video.blogger.instagramUsername}</span>
        </span>
        <span className="text-app-hint">
          {video.blogger.fullName} · {formatNumber(video.bloggerFollowers)} obunachi
        </span>
        <span className="inline-flex items-center gap-1.5 text-app-hint">
          <Phone size={13} aria-hidden="true" />
          {formatPhoneDisplay(video.bloggerPhone)}
        </span>
      </div>

      <button
        type="button"
        onClick={() => onOpenBlogger(video.blogger.id)}
        className="mt-2 inline-flex min-h-11 items-center gap-1 text-sm font-medium text-brand"
      >
        Bloger kartochkasi
        <ChevronRight size={14} aria-hidden="true" />
      </button>
    </div>
  )
}

/**
 * `docs/03`: "[▶️ Telegram'da ochish] tugmasi arxiv kanaldagi nusxaga t.me/c/<kanal>/<xabar>
 * havolasini ochadi — video Telegram'da o'ynaydi ... panelda ichki pleyer yo'q." Bot API'ning
 * `getFile` 20 MB cheklovi shu yo'l bilan aylanib o'tiladi.
 *
 * Havola oddiy `<a href>` EMAS, `openArchiveLink()` (`lib/telegram.ts`) orqali ochiladi — panel
 * Telegram Mini App ICHIDA ishlagani uchun `t.me` havolasi SDK'ning `openTelegramLink()`
 * funksiyasi bilan ochilishi kerak, aks holda ba'zi mijozlarda panel holati buzilishi mumkin.
 */
function VideoPlaybackSection({ archiveUrl }: { archiveUrl: string | null }) {
  return (
    <section className="flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-4">
      <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
        <Clapperboard size={16} className="text-brand" aria-hidden="true" />
        Video
      </h2>
      {archiveUrl ? (
        <Button className="w-full" onClick={() => openArchiveLink(archiveUrl)}>
          <Play size={16} aria-hidden="true" />
          Telegram&apos;da ochish
        </Button>
      ) : (
        <p className="text-sm text-app-hint">
          Video hali arxiv kanalga ko&apos;chirilmagan — birozdan so&apos;ng qayta urinib
          ko&apos;ring.
        </p>
      )}
    </section>
  )
}

interface InstagramSectionProps {
  video: VideoDetailData
  isPending: boolean
  errorMessage: string | null
  onVerifyOk: () => void
  onVerifyReject: () => void
}

/**
 * `docs/03-app-flow.md` "Havola tekshiruvi (admin tomonida)": havola avtomatik qabul
 * qilinadi, lekin admin ochib ko'rishi kutiladi — noto'g'ri/begona reel 15-kungacha
 * yotib qolmasin (aks holda bloger 2 hafta "ishlayapman" deb yurib, keyin rad javobini
 * oladi). Shu sabab bu bo'lim faqat linkni ko'rsatib qo'ymaydi — ochilgandan keyingi
 * qarorni ham ('to'g'ri' / 'ochilmadi') SHU YERDA so'raydi, boshqa alohida ekran YO'Q.
 *
 * `canVerify` server (`verifyVideoLink`) qonuniylik shartining aynan o'zi: havola bor,
 * hali tekshirilmagan, `stage` `POSTED`/`CHECKING`. Boshqa holatda (masalan video allaqachon
 * `PAID`/`REJECTED`) taklif ko'rsatilmaydi — tugma bossa ham 409 qaytishi mumkin bo'lgan
 * holatni oldindan yashiradi.
 */
function InstagramSection({
  video,
  isPending,
  errorMessage,
  onVerifyOk,
  onVerifyReject,
}: InstagramSectionProps) {
  const canVerify =
    video.instagramUrl !== null &&
    !video.linkVerified &&
    (video.stage === VideoStage.POSTED || video.stage === VideoStage.CHECKING)
  const linkUrgency = linkVerifyUrgency(video)

  return (
    <section className="flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-4">
      <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
        <Instagram size={16} className="text-brand" aria-hidden="true" />
        Instagram
      </h2>
      {video.instagramUrl ? (
        <>
          <a
            href={video.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-11 items-center justify-between gap-3 rounded-button border border-app-border bg-app-bg px-3 py-2 text-sm font-medium transition-colors duration-150 ease-out hover:border-brand hover:text-brand"
          >
            <span className="truncate">{video.instagramUrl.replace(/^https?:\/\//, '')}</span>
            <span className="inline-flex shrink-0 items-center gap-1 text-brand">
              <ExternalLink size={14} aria-hidden="true" />
              Ochish
            </span>
          </a>
          {video.postedAt && (
            <span className="text-xs text-app-hint">Joylangan: {formatDateUz(video.postedAt)}</span>
          )}

          {canVerify && (
            <div className="mt-1 flex flex-col gap-2 rounded-button border border-urgency-overdue/30 bg-urgency-overdue/5 p-3">
              <p
                className={cn(
                  'inline-flex items-center gap-1.5 text-xs font-medium',
                  linkUrgency === 'overdue'
                    ? 'text-urgency-overdue-text'
                    : 'text-urgency-today-text',
                )}
              >
                <TriangleAlert size={12} className="shrink-0" aria-hidden="true" />
                Havolani yuqorida oching va tekshiring: kitob haqidagi to&apos;g&apos;ri reelmi?
                {linkUrgency === 'overdue' ? ' 48 soatdan oshib ketdi!' : ''}
              </p>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1" onClick={onVerifyOk} disabled={isPending}>
                  <CircleCheck size={14} aria-hidden="true" />
                  Havola to&apos;g&apos;ri
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  className="flex-1"
                  onClick={onVerifyReject}
                  disabled={isPending}
                >
                  <TriangleAlert size={14} aria-hidden="true" />
                  Havola ochilmadi
                </Button>
              </div>
              {errorMessage && (
                <p className="text-xs font-medium text-stage-rejected-text">{errorMessage}</p>
              )}
            </div>
          )}

          {video.linkVerified && (
            <p className="inline-flex items-center gap-1.5 text-xs font-medium text-urgency-done-text">
              <CircleCheck size={12} aria-hidden="true" />
              Havola tekshirildi
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-app-hint">Bloger hali Instagram havolasini yubormagan.</p>
      )}
    </section>
  )
}

// `ChecksSection` — Bosqich 9'da `VideoChecksSection.tsx`ga ko'chirildi: kelayotgan tekshiruv
// endi kiritish maydoni (jonli hisob bilan), yakunlangan tekshiruvlar esa ✏️ tuzatish tugmasi
// oladi. Import qarang: `import { ChecksSection, EditCheckModal } from './VideoChecksSection'`.

function FinanceSection({ video }: { video: VideoDetailData }) {
  return (
    <section className="flex flex-col gap-3 rounded-card border border-app-border bg-app-card p-4">
      <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
        <Wallet size={16} className="text-brand" aria-hidden="true" />
        Hisob
      </h2>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <FinanceStat
          label="Stavka"
          value={video.ratePer1000 !== null ? `${formatSom(video.ratePer1000)} / 1000` : '—'}
        />
        <FinanceStat
          label="Oxirgi prosmotr"
          value={video.views !== null ? formatNumber(video.views) : '—'}
        />
        <FinanceStat label="Summa" value={video.amount !== null ? formatSom(video.amount) : '—'} />
      </dl>
      {video.finalizedAt && (
        <p className="inline-flex items-center gap-1.5 rounded-badge bg-urgency-done/10 px-2 py-1 text-xs font-medium text-urgency-done-text">
          <CircleCheck size={12} aria-hidden="true" />
          Yakunlandi: {formatNumber(video.finalViews ?? 0)} → {formatSom(video.finalAmount ?? 0)}
        </p>
      )}
      {isAwaitingLink(video) && (
        <p className={cn('text-xs font-medium', VIDEO_URGENCY_META[video.urgency].textClass)}>
          Havola kutilmoqda
          {(() => {
            const label = urgencyLabel(video.urgency, video.daysUntilCheck)
            return label ? ` · ${label}` : ''
          })()}
        </p>
      )}
    </section>
  )
}

function FinanceStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-app-hint">{label}</dt>
      <dd className="tabular text-base font-semibold text-brand">{value}</dd>
    </div>
  )
}

interface CommentsSectionProps {
  comments: CommentItem[]
  commentText: string
  onCommentTextChange: (value: string) => void
  onSubmit: (event: FormEvent) => void
  isSubmitting: boolean
  errorMessage: string | null
}

function CommentsSection({
  comments,
  commentText,
  onCommentTextChange,
  onSubmit,
  isSubmitting,
  errorMessage,
}: CommentsSectionProps) {
  const sorted = [...comments].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )

  return (
    <section className="flex flex-col gap-3 rounded-card border border-app-border bg-app-card p-4">
      <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
        <MessageSquare size={16} className="text-brand" aria-hidden="true" />
        Izohlar
      </h2>

      <form onSubmit={onSubmit} className="flex flex-col gap-2">
        <textarea
          value={commentText}
          onChange={(event) => onCommentTextChange(event.target.value)}
          rows={2}
          placeholder="Izoh yozish..."
          className="rounded-input border border-app-border bg-app-bg px-3 py-2 text-base text-app-text outline-none focus-visible:border-brand"
        />
        <div className="flex items-center justify-between gap-2">
          {errorMessage ? (
            <span className="text-xs text-stage-rejected-text">{errorMessage}</span>
          ) : (
            <span />
          )}
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            disabled={isSubmitting || commentText.trim().length === 0}
          >
            {isSubmitting ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Send size={14} aria-hidden="true" />
            )}
            Yuborish
          </Button>
        </div>
      </form>

      {sorted.length === 0 ? (
        <p className="text-sm text-app-hint">Hali izoh yo&apos;q</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {sorted.map((comment) => (
            <li key={comment.id} className="text-sm">
              <p className="text-app-text">{comment.text}</p>
              <p className="mt-0.5 text-xs text-app-hint">{formatRelativeUz(comment.createdAt)}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

interface DecisionFooterProps {
  stage: VideoStage
  moderatedAt: string | null
  isPending: boolean
  errorMessage: string | null
  onApproveClick: () => void
  onRejectClick: () => void
}

/**
 * Asosiy amallar — modal `shrink-0` footeri, hech qachon skroll ortida qolmaydi (375px'da
 * ham). Faqat HALI KO'RIB CHIQILMAGAN moderatsiya yozuvida ko'rsatiladi: `stage === MODERATION
 * && moderatedAt === null`. E'tibor bering — "havola kutilmoqda" holati ham `stage ===
 * MODERATION`, lekin `moderatedAt` allaqachon to'ldirilgan (admin allaqachon qaror qabul
 * qilgan) — bunday holatda qayta Tasdiqlash/Rad etish tugmalarini ko'rsatish noto'g'ri bo'lardi.
 */
function DecisionFooter({
  stage,
  moderatedAt,
  isPending,
  errorMessage,
  onApproveClick,
  onRejectClick,
}: DecisionFooterProps) {
  const showActions = stage === VideoStage.MODERATION && moderatedAt === null

  return (
    <div className="shrink-0 border-t border-app-border bg-app-card p-4">
      {errorMessage && (
        <p className="mb-2 text-xs font-medium text-stage-rejected-text">{errorMessage}</p>
      )}
      {showActions ? (
        <div className="flex gap-2">
          <Button className="flex-1" onClick={onApproveClick} disabled={isPending}>
            <CircleCheck size={16} aria-hidden="true" />
            Tasdiqlash
          </Button>
          <Button variant="danger" className="flex-1" onClick={onRejectClick} disabled={isPending}>
            <XCircle size={16} aria-hidden="true" />
            Rad etish
          </Button>
        </div>
      ) : (
        <p className="w-full py-2 text-center text-sm text-app-hint">
          {stage === VideoStage.MODERATION
            ? 'Video tasdiqlangan — bloger havola yuborishini kutamiz.'
            : "Bu bosqichda moderatsiya amali yo'q."}
        </p>
      )}
    </div>
  )
}

/** Yuklanish paytidagi skeleton — header/scroll/footer bilan bir xil joylashuvda. */
function DetailSkeleton() {
  return (
    // `aria-busy` — skrinrider uchun: modal yuklanayotganda jim turmasin.
    <div className="flex h-full flex-col" aria-busy="true">
      <div className="shrink-0 border-b border-app-border p-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="mt-3 h-4 w-32" />
        <Skeleton className="mt-2 h-4 w-24" />
      </div>
      <div className="flex flex-1 flex-col gap-4 overflow-hidden p-4">
        <Skeleton className="h-16" />
        <Skeleton className="h-20" />
        <Skeleton className="h-28" />
        <Skeleton className="h-24" />
      </div>
      <div className="shrink-0 border-t border-app-border p-4">
        <Skeleton className="h-11" />
      </div>
    </div>
  )
}

function DetailErrorState({
  error,
  onRetry,
  onClose,
}: {
  error: Error | null
  onRetry: () => void
  onClose: () => void
}) {
  const isServerError = error instanceof ApiError
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
      <CircleX size={28} className="text-app-hint" aria-hidden="true" />
      <p className="text-sm text-app-hint">
        {isServerError ? error.message : "Ma'lumotni yuklab bo'lmadi. Internetni tekshiring."}
      </p>
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onClose}>
          Yopish
        </Button>
        <Button onClick={onRetry}>Qayta urinish</Button>
      </div>
    </div>
  )
}
